import flow from 'lodash/flow';
import memoizeOne from 'memoize-one';
import type { PreloadedQuery } from 'react-relay';
import { withAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsSubject } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import ReduxAnalyticsData from '@atlassian/jira-analytics-web-react/src/components/redux-analytics-data.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import type { viewBacklogVersionFilterRelayQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import type { PrefetchedDataGql } from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types.tsx';
import type { JiraPlanDataRestResponse } from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import { ISSUE_PARENT_PANEL, VERSION_PANEL } from '../model/panels/constants.tsx';
import type { PanelType } from '../model/panels/types.tsx';
import {
	BACKLOG_INTERACTIVE_MARK,
	BACKLOG_READY_FOR_USER_MARK,
} from '../model/performance/constants.tsx';
import type { CardId, CardKey, CardList as CardListType } from '../model/types.tsx';
import { backlogView } from '../state/actions/app/index.tsx';
import { cardListsEnableAll, cardListsDisable } from '../state/actions/card-list/index.tsx';
import { cardSingleSelect } from '../state/actions/cards/selection/index.tsx';
import { togglePanel } from '../state/actions/panels/index.tsx';
import { getAttributes } from '../state/analytics/index.tsx';
import type { EpicIssueTypeState } from '../state/reducers/config/epic/types.tsx';
import type { CardListOrderEntityState } from '../state/reducers/entities/card-list-order/types.tsx';
import {
	getRapidViewId,
	getProjectKey,
	getBaseUrl,
	getSessionId,
	getStoryPointFieldId,
	getEpicIssueType,
} from '../state/selectors/app/index.tsx';
import {
	getVisibleCardListIds,
	shouldShowDetailView,
	getSelectedCards,
	makeCardKeyFromCardId,
	getCardLists,
	getCollapsedCardLists,
	makeGetSourceCardListId,
	makeGetCardHierarchyLevel,
	makeGetSourceCardGroupId,
} from '../state/selectors/cards/index.tsx';
import { issueHierarchyLevelSelector } from '../state/selectors/issue-parent/index.tsx';
import type { State, Dispatch } from '../state/types.tsx';
import Backlog, { type PropsWithFields } from './view.tsx';

export type OwnProps = {
	isCacheHit: boolean;
	prefetchedData: PrefetchedDataGql | null;
	prefetchedPlanData: JiraPlanDataRestResponse | null;
	customFiltersQueryRef: PreloadedQuery<viewBacklogCustomFiltersQuery> | null | undefined;
	versionFilterRelayQueryRef: PreloadedQuery<viewBacklogVersionFilterRelayQuery> | null | undefined;
};

export type StateProps = {
	isDetailViewVisible: boolean;
	issueHierarchyLevel: number;
	boardId: number;
	baseUrl: string;
	projectKey: string;
	storyPointFieldId: string | null;
	epicIssueType: EpicIssueTypeState | null;
	cardListIds: CardListOrderEntityState;
	cardLists: {
		[cardListId: string]: CardListType;
	};
	collapsedCardLists: {
		[cardListId: string]: boolean;
	};
	selectedCards: CardId[];
	backlogSessionId: string;
	getCardKeyFromCardId: (cardId: CardId) => CardKey;
	getSourceCardListId: (cardId: CardId) => string | undefined;
	getSourceCardGroupId: (cardListId: string | undefined, cardId: number) => string | undefined;
	getCardHierarchyLevel: (cardId: CardId) => number | undefined;
};

export type DispatchProps = {
	onBacklogView: (analyticsEvent: UIAnalyticsEvent) => void;
	onCardListsDisable: (cardListIds: string[]) => void;
	onCardListsEnableAll: () => void;
	onCardSingleSelect: (cardId: CardId) => void;
	onTogglePanel: (panel: PanelType) => void;
};

export const mapStateToProps = (state: State): StateProps => ({
	storyPointFieldId: getStoryPointFieldId(state),
	epicIssueType: getEpicIssueType(state),
	cardListIds: getVisibleCardListIds(state),
	cardLists: getCardLists(state),
	selectedCards: getSelectedCards(state),
	isDetailViewVisible: shouldShowDetailView(state),
	getCardKeyFromCardId: makeCardKeyFromCardId(state),
	collapsedCardLists: getCollapsedCardLists(state),
	boardId: Number(getRapidViewId(state)),
	baseUrl: getBaseUrl(state),
	projectKey: getProjectKey(state),
	backlogSessionId: getSessionId(state),
	issueHierarchyLevel: issueHierarchyLevelSelector(state)(),
	getSourceCardListId: makeGetSourceCardListId(state),
	getSourceCardGroupId: makeGetSourceCardGroupId(state),
	getCardHierarchyLevel: makeGetCardHierarchyLevel(state),
});

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
	onBacklogView: (analyticsEvent: UIAnalyticsEvent) => {
		dispatch(backlogView(analyticsEvent));
	},
	onCardListsDisable: (cardListIds: string[]) => {
		dispatch(cardListsDisable(cardListIds));
	},
	onCardListsEnableAll: () => {
		dispatch(cardListsEnableAll());
	},
	onCardSingleSelect: (cardId: CardId) => {
		dispatch(cardSingleSelect(cardId));
	},
	onTogglePanel: (panel: PanelType) => {
		dispatch(togglePanel(panel));
	},
});

const onPanelClose =
	({ onTogglePanel }: Pick<DispatchProps, 'onTogglePanel'>, panelType: PanelType) =>
	(): void => {
		onTogglePanel(panelType);
	};

const onBacklogViewFactory = memoizeOne(
	(onBacklogView: (analyticsEvent: UIAnalyticsEvent) => void) =>
		(analyticsEvent: UIAnalyticsEvent) => {
			setMark(BACKLOG_READY_FOR_USER_MARK);
			setMark(BACKLOG_INTERACTIVE_MARK);

			onBacklogView(analyticsEvent);
		},
);

const onEpicPanelCloseFactory = memoizeOne(
	(onTogglePanel: DispatchProps['onTogglePanel'], panelType: PanelType) =>
		onPanelClose({ onTogglePanel }, panelType),
);

const onVersionPanelCloseFactory = memoizeOne(
	(onTogglePanel: DispatchProps['onTogglePanel'], panelType: PanelType) =>
		onPanelClose({ onTogglePanel }, panelType),
);

export const mergeProps = (
	{
		storyPointFieldId,
		epicIssueType,
		cardListIds,
		isDetailViewVisible,
		boardId,
		baseUrl,
		projectKey,
		issueHierarchyLevel,
	}: StateProps,
	{ onBacklogView, onTogglePanel }: DispatchProps,
	{
		isCacheHit,
		prefetchedData,
		prefetchedPlanData,
		customFiltersQueryRef,
		versionFilterRelayQueryRef,
	}: OwnProps,
): PropsWithFields => ({
	isCacheHit,
	prefetchedData,
	prefetchedPlanData,
	storyPointFieldId,
	epicIssueType,
	cardListIds,
	isDetailViewVisible,
	boardId,
	baseUrl,
	projectKey,
	issueHierarchyLevel,
	onBacklogView: onBacklogViewFactory(onBacklogView),
	onEpicPanelClose: onEpicPanelCloseFactory(onTogglePanel, ISSUE_PARENT_PANEL),
	onVersionPanelClose: onVersionPanelCloseFactory(onTogglePanel, VERSION_PANEL),
	customFiltersQueryRef,
	versionFilterRelayQueryRef,
});

export default flow(
	withAnalyticsEvents(),
	ReduxAnalyticsData((state: State) => ({
		attributes: getAttributes(state),
	})),
	AnalyticsSubject('backlog'),
	connect(mapStateToProps, mapDispatchToProps, mergeProps),
)(Backlog);
